import {
  Alert,
  AlertIcon,
  Button,
  ButtonGroup,
  Checkbox,
  FormControl,
  FormLabel,
  Input,
  Text,
  Textarea,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useForm } from 'react-hook-form';
import { djs } from '@duke-hq/djs';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { DialogBox } from '@duke-hq/ui/components/DialogBox/DialogBox';
import { StudentCourseEnrolmentStatus } from '@duke-hq/svc-api-db/client';

interface ApproveReenrolmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  taskId: string;
  blockingEnrolmentId: number | null;
  blockingEnrolmentStatus: string | null;
  proposedEnrolmentId: number | null;
  proposedCourseName: string | null;
  blockingCourseName: string | null;
}

const terminalStatusLabel: Record<string, string> = {
  [StudentCourseEnrolmentStatus.COMPLETED]: 'completed',
  [StudentCourseEnrolmentStatus.WITHDRAWN]: 'withdrawn',
};

interface ApproveReenrolmentFormData {
  startDate: string;
  notes: string;
  notifyStudentAndConsultant: boolean;
}

export function ApproveReenrolmentModal({
  isOpen,
  onClose,
  taskId,
  blockingEnrolmentId,
  blockingEnrolmentStatus,
  proposedEnrolmentId,
  proposedCourseName,
  blockingCourseName,
}: ApproveReenrolmentModalProps) {
  const utils = trpcAdmin.useUtils();
  const toast = useToast();

  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<ApproveReenrolmentFormData>({
    defaultValues: {
      startDate: djs().format('YYYY-MM-DD'),
      notes: '',
      notifyStudentAndConsultant: true,
    },
  });

  const approveMutation = trpcAdmin.support.task.approveReenrolment.useMutation({
    onSuccess: () => {
      toast({
        status: 'success',
        title: t`Re-enrolment approved successfully`,
      });
      void utils.support.task.get.invalidate();
      void utils.support.task.list.invalidate(undefined, { refetchType: 'active' });
      void utils.students.enrollmentsById.invalidate();
      reset();
      onClose();
    },
    onError: (error) => {
      toast({
        status: 'error',
        title: t`Failed to approve re-enrolment`,
        description: error.message,
      });
    },
  });

  const onSubmit = (data: ApproveReenrolmentFormData) => {
    if (blockingEnrolmentId == null || proposedEnrolmentId == null) {
      return;
    }

    approveMutation.mutate({
      taskId,
      blockingEnrolmentId,
      proposedEnrolmentId,
      startDate: data.startDate,
      notes: data.notes || undefined,
      notifyStudentAndConsultant: data.notifyStudentAndConsultant,
    });
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const blockingEnrolmentAlreadyResolved =
    blockingEnrolmentStatus === StudentCourseEnrolmentStatus.COMPLETED ||
    blockingEnrolmentStatus === StudentCourseEnrolmentStatus.WITHDRAWN;

  const canSubmit = blockingEnrolmentId != null && proposedEnrolmentId != null;

  return (
    <DialogBox
      isOpen={isOpen}
      onClose={handleClose}
      header={t`Approve Re-enrolment`}
      body={
        <VStack align="stretch" spacing={4}>
          <Alert status="info" borderRadius="md">
            <AlertIcon />
            <Text fontSize="sm">
              {blockingEnrolmentAlreadyResolved ? (
                <Trans>
                  The current enrolment
                  {blockingCourseName != null ? ` (${blockingCourseName})` : ''} has already been{' '}
                  {terminalStatusLabel[blockingEnrolmentStatus]}. This will activate the proposed
                  enrolment
                  {proposedCourseName != null ? ` (${proposedCourseName})` : ''}.
                </Trans>
              ) : (
                <Trans>
                  This will withdraw the current enrolment
                  {blockingCourseName != null ? ` (${blockingCourseName})` : ''} and activate the
                  proposed enrolment
                  {proposedCourseName != null ? ` (${proposedCourseName})` : ''}.
                </Trans>
              )}
            </Text>
          </Alert>

          <FormControl>
            <FormLabel>
              <Trans>Enrolment start date</Trans>
            </FormLabel>
            <Input {...register('startDate')} type="date" min={djs().format('YYYY-MM-DD')} />
          </FormControl>

          <FormControl>
            <FormLabel>
              <Trans>Notes</Trans>
            </FormLabel>
            <Textarea
              {...register('notes')}
              placeholder={t`Add any notes about this approval`}
              rows={3}
            />
          </FormControl>

          <Checkbox {...register('notifyStudentAndConsultant')} defaultChecked>
            <Text fontSize="sm">
              <Trans>Notify student and consultant</Trans>
            </Text>
          </Checkbox>
        </VStack>
      }
      footer={
        <ButtonGroup>
          <Button variant="secondary" onClick={handleClose}>
            <Trans>Cancel</Trans>
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            isLoading={isSubmitting || approveMutation.isPending}
            isDisabled={!canSubmit}
          >
            <Trans>Approve Re-enrolment</Trans>
          </Button>
        </ButtonGroup>
      }
    />
  );
}
